import { useState, useEffect } from 'react'
import { Form, Formik } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import * as Yup from 'yup'

import Layout from 'components/Layout'
import Loader from 'components/Loader'
import ListFilters from 'components/VenueSettings/ListFilters'
import VenueSettingsComponent from 'components/VenueSettings'
import ConfirmationModal from 'components/VenueSettings/ConfirmationModal'
import TipConfigurationsForm from 'components/VenueSettings/Drawers/TipConfigurations'

import { fetchSettingsWithDefaultSettings } from 'store/Settings/actions'
import { createVenueSettingsNew } from 'store/Settings/actions'
import {
  getIsVenueSettingsLoading,
  getVenueSettings,
  getVenueDefaultSettings,
} from 'store/Settings/selectors'
import { getCurrentVenueUuid } from 'store/Venues/selector'
import settingsUtils from 'utils/settings'
import { validationRegex, errorMessages } from 'utils/validations'
import { ORG_NAME } from 'utils/env'
import { VENUE_SETTINGS_FIELDS_REQUIRED_CONFIRMATION } from 'resources'
import { venueSettingsModals } from 'components/VenueSettings/resources'
import { loadRevenueCenters } from 'store/RevenueCenters/actions'

const VenueSettings = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [isOpenDialogModal, setIsOpenDialogModal] = useState(false)
  const [showValidationErrors, setShowValidationErrors] =
    useState(false)
  const [modalType, setModalType] = useState(null)

  const dispatch = useDispatch()

  const currentVenueUuid = useSelector(getCurrentVenueUuid)
  const isVenueSettingLoading = useSelector(getIsVenueSettingsLoading)
  const venueSettings = useSelector(getVenueSettings)
  const venueDefaultSettings = useSelector(getVenueDefaultSettings)

  const settings = settingsUtils.determineValidVenueSettingsLevel({
    settings: venueSettings,
    defaultSettings: venueDefaultSettings,
    venueUuid: currentVenueUuid,
    organizationName: ORG_NAME,
    extraFilters: {
      configurationModelId: null,
      deactivatedAt: null,
    },
  })

  const transformedSettingsValues =
    settingsUtils.transformVenueSettingsValues(settings)

  useEffect(() => {
    dispatch(fetchSettingsWithDefaultSettings(currentVenueUuid))
    dispatch(loadRevenueCenters(currentVenueUuid))
  }, [currentVenueUuid, dispatch])

  const initialValues = {
    force_offline_mode:
      transformedSettingsValues?.force_offline_mode ?? false,
    alcohol_limit_message:
      transformedSettingsValues?.alcohol_limit_message ?? '',
    alcohol_limit_per_order:
      transformedSettingsValues?.alcohol_limit_per_order ?? '',
    check_recent_order:
      transformedSettingsValues?.check_recent_order ?? false,
    confirmation_number_length:
      transformedSettingsValues?.confirmation_number_length ?? 0,
    enable_inventory_management:
      transformedSettingsValues?.enable_inventory_management ?? true,
    allow_cash_payments_on_pos:
      transformedSettingsValues?.allow_cash_payments_on_pos ?? true,
    allow_ticket_payments_on_pos:
      transformedSettingsValues?.allow_ticket_payments_on_pos ?? true,
    allow_gift_card_payments_on_pos:
      transformedSettingsValues?.allow_gift_card_payments_on_pos ??
      true,
    confirm_default_tip:
      transformedSettingsValues?.confirm_default_tip ?? false,
    // when api return qr_tender as null we have to convert it to '#default_empty_value' in order to
    // display "Default" value in Select, as well we convert it back when send request to api
    qr_tender:
      transformedSettingsValues?.qr_tender ?? '#default_empty_value',
    enable_pos_sms_receipts:
      transformedSettingsValues?.enable_pos_sms_receipts ?? false,
    sms_receipt_message:
      transformedSettingsValues?.sms_receipt_message ?? '',
    require_payment_for_pos_tabs:
      transformedSettingsValues?.require_payment_for_pos_tabs ??
      false,
    enable_pos_email_receipts:
      transformedSettingsValues?.enable_pos_email_receipts ?? false,
    require_manager_for_pos_config:
      transformedSettingsValues?.require_manager_for_pos_config ??
      false,
  }

  const validationSchema = Yup.object().shape({
    sms_receipt_message: Yup.string().when(
      'enable_pos_sms_receipts',
      {
        is: true,
        then: Yup.string()
          .matches(
            validationRegex.receiptUri,
            errorMessages.receiptUri
          )
          .required('This field is required'),
        otherwise: Yup.string(),
      }
    ),
  })

  const onCloseModal = (setFieldValue) => {
    setIsOpenDialogModal(false)

    setFieldValue('force_offline_mode', false)
  }

  const handleConfirmationCallback = (values) => {
    const venueSettingsToSend =
      settingsUtils.renderFieldsToCreateSettings(
        values,
        initialValues,
        currentVenueUuid
      )

    dispatch(createVenueSettingsNew(venueSettingsToSend))
  }

  const handleSubmit = (values) => {
    const venueSettingsToSend =
      settingsUtils.renderFieldsToCreateSettings(
        values,
        initialValues,
        currentVenueUuid
      )

    const venueSettingsToSendNames = venueSettingsToSend.map(
      ({ name }) => name
    )

    if (
      venueSettingsToSendNames.some((name) =>
        VENUE_SETTINGS_FIELDS_REQUIRED_CONFIRMATION.includes(name)
      ) &&
      VENUE_SETTINGS_FIELDS_REQUIRED_CONFIRMATION.some(
        (field) => !initialValues[field]
      )
    ) {
      return setIsOpenDialogModal(true)
    }

    dispatch(createVenueSettingsNew(venueSettingsToSend))
    setShowValidationErrors(false)
  }

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue }) => {
          return (
            <Form className="venue-settings-form">
              <Layout
                isSidebar
                navbarContent={
                  <ListFilters
                    handleSearch={setSearchQuery}
                    searchQuery={searchQuery}
                    setShowValidationErrors={setShowValidationErrors}
                  />
                }
                mainContent={
                  isVenueSettingLoading ? (
                    <Loader />
                  ) : (
                    <>
                      <VenueSettingsComponent
                        searchQuery={searchQuery}
                        setModalType={setModalType}
                        showValidationErrors={showValidationErrors}
                      />
                      {isOpenDialogModal && (
                        <ConfirmationModal
                          onCloseModal={onCloseModal}
                          handleConfirmationCallback={
                            handleConfirmationCallback
                          }
                          setFieldValue={setFieldValue}
                          values={values}
                          isOpenDialogModal={isOpenDialogModal}
                        />
                      )}
                    </>
                  )
                }
              />
            </Form>
          )
        }}
      </Formik>
      {modalType === venueSettingsModals.TIP_CONFIGURATIONS && (
        <TipConfigurationsForm
          setModalType={setModalType}
          transformedSettingsValues={transformedSettingsValues}
        />
      )}
    </>
  )
}

export default VenueSettings
